import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, BookOpen, User, Clock, MessageSquare, FileText, Bot } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import AdminLayout from "@/components/layout/AdminLayout";
import { fetchChatSession, fetchChatMessages } from "@/api/chat";
import { ChatMessageList } from "@/components/chat/ChatMessageList";

export default function AIChatSessionDetail() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const id = Number(sessionId);

  const { data: session, isLoading: sessionLoading } = useQuery({
    queryKey: ["chat-session", id],
    queryFn: () => fetchChatSession(id),
    enabled: !!id,
  });

  const { data: messages = [], isLoading: messagesLoading } = useQuery({
    queryKey: ["chat-messages", id],
    queryFn: () => fetchChatMessages(id),
    enabled: !!id,
  });

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });

  return (
    <AdminLayout
      title={session?.title || "Chat Session"}
      actionButton={
        <Button variant="outline" onClick={() => navigate("/admin/ask-ai")}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Sessions
        </Button>
      }
    >
      {sessionLoading ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          Loading session...
        </div>
      ) : session ? (
        <div className="space-y-6">
          {/* Session Metadata */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Session Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="space-y-1">
                  <p className="text-muted-foreground flex items-center gap-1">
                    <BookOpen className="w-3.5 h-3.5" /> Book
                  </p>
                  <p className="font-medium">
                    {session.book_title || `Book #${session.book_id}`}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-muted-foreground flex items-center gap-1">
                    <User className="w-3.5 h-3.5" /> User
                  </p>
                  <p className="font-medium">
                    {session.user_name || session.user_email || session.user_id.slice(0, 8)}
                  </p>
                  <Badge
                    variant={
                      session.subscription_status === "active" ? "default" :
                      session.subscription_status === "expired" ? "destructive" :
                      session.subscription_status === "cancelled" ? "destructive" :
                      "outline"
                    }
                    className="text-[10px]"
                  >
                    {session.subscription_status || "free"}
                  </Badge>
                </div>
                <div className="space-y-1">
                  <p className="text-muted-foreground flex items-center gap-1">
                    <MessageSquare className="w-3.5 h-3.5" /> Messages
                  </p>
                  <p className="font-medium">{session.message_count}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-muted-foreground flex items-center gap-1">
                    <Bot className="w-3.5 h-3.5" /> Persona
                  </p>
                  <p>
                    <Badge variant="secondary">{session.persona || "librarian"}</Badge>
                    {session.chat_type && (
                      <Badge variant="outline" className="ml-1">{session.chat_type}</Badge>
                    )}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-muted-foreground flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" /> Created
                  </p>
                  <p>{formatDate(session.created_at)}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-muted-foreground flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" /> Last Activity
                  </p>
                  <p>{formatDate(session.updated_at)}</p>
                </div>
              </div>

              {session.context_summary && (
                <>
                  <Separator className="my-4" />
                  <div className="space-y-1">
                    <p className="text-muted-foreground text-sm flex items-center gap-1">
                      <FileText className="w-3.5 h-3.5" /> Context Summary
                    </p>
                    <p className="text-sm bg-muted rounded-md p-3">
                      {session.context_summary}
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Message History */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Message History</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="h-[500px] flex flex-col">
                <ChatMessageList
                  messages={messages}
                  isLoading={messagesLoading}
                  isReadOnly
                />
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="text-center py-12 text-muted-foreground">
          Session not found.
        </div>
      )}
    </AdminLayout>
  );
}
